import { create } from "zustand";
import { persist } from "zustand/middleware";

import { supabase } from "~/integrations/supabase/client";
import { UserRole } from "~/types/user";

export interface ProfileState {
  id: string | null;
  name: string | null;
  lastname: string | null;
  email: string | null;
  phone: string | null;
  role: UserRole | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchProfile: () => Promise<void>;
  clearProfile: () => void;
  setProfileData: (data: {
    id: string;
    name: string | null;
    lastname: string | null;
    email: string | null;
    role: UserRole | null;
  }) => void;
  getUserId: () => string | null;
  forceSyncProfileCheck: () => Promise<string | null>;
}

export const useProfileStore = create<ProfileState>()(
  persist(
    (set, get) => ({
      id: null,
      name: null,
      lastname: null,
      email: null,
      phone: null,
      role: null,
      isLoading: false,
      error: null,

      fetchProfile: async () => {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.user) {
          set({ error: "No authenticated user found" });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const userId = session.user.id;

          // Fetch profile data from the profiles table including activation status
          const { data, error } = await supabase
            .from("profiles")
            .select(
              "id, name, lastname, email, phone, role, is_active, deactivated_at"
            )
            .eq("id", userId)
            .single();

          if (error) {
            console.error("Error fetching profile:", error);
            set({ error: error.message, isLoading: false });
            return;
          }

          if (data) {
            // Check if user is deactivated
            if (!data.is_active) {
              console.log("User account is deactivated");
              set({
                error:
                  "Su cuenta ha sido desactivada. Contacte al administrador.",
                isLoading: false,
              });

              // Sign out the deactivated user
              await supabase.auth.signOut();
              return;
            }

            set({
              id: data.id,
              name: data.name,
              lastname: data.lastname,
              email: data.email,
              phone: data.phone,
              role: data.role as UserRole,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error("Unexpected error fetching profile:", error);
          set({
            error: error instanceof Error ? error.message : "Unknown error",
            isLoading: false,
          });
        }
      },

      clearProfile: () => {
        set({
          id: null,
          name: null,
          lastname: null,
          email: null,
          phone: null,
          role: null,
          error: null,
          isLoading: false,
        });
      },

      setProfileData: (data) => {
        set({
          id: data.id,
          name: data.name,
          lastname: data.lastname,
          email: data.email,
          role: data.role,
        });
      },

      getUserId: () => {
        return get().id;
      },

      forceSyncProfileCheck: async () => {
        // Direct synchronous check for user ID from session
        console.log("Force sync profile check initiated");

        try {
          // Get the current session
          const { data: sessionData } = await supabase.auth.getSession();

          if (!sessionData.session?.user?.id) {
            console.warn("No user ID found in session during force check");
            return null;
          }

          const userId = sessionData.session.user.id;

          // If we already have this ID in the store, just return it
          if (get().id === userId) {
            console.log("User ID already in store:", userId);
            return userId;
          }

          console.log(
            "User ID found in session but not in store, fetching profile data"
          );

          // Fetch user profile data by user ID
          const { data: profileData, error } = await supabase
            .from("profiles")
            .select("id, name, lastname, email, phone, role")
            .eq("id", userId)
            .single();

          if (error) {
            console.error("Error during force profile check:", error);
            return null;
          }

          if (profileData) {
            // Update store with profile data
            set({
              id: profileData.id,
              name: profileData.name,
              lastname: profileData.lastname,
              email: profileData.email,
              phone: profileData.phone,
              role: profileData.role as UserRole,
              isLoading: false,
              error: null,
            });

            console.log("Profile force updated with ID:", profileData.id);
            return profileData.id;
          }

          return null;
        } catch (err) {
          console.error("Unexpected error during force profile check:", err);
          return null;
        }
      },
    }),
    {
      name: "profile-storage", // name of the item in localStorage
      skipHydration: false, // load from localStorage on page load
    }
  )
);
